import { useEffect, useRef } from 'react';
import { Marker as MapLibreMarker, type MarkerOptions, Popup } from 'maplibre-gl';
import { useMapContext } from './MapContext';

export interface MapMarkerProps {
  /** [lng, lat] */
  position: [number, number];
  color?: string;
  size?: number;
  onClick?: () => void;
  popup?: string;
  draggable?: boolean;
  onDragEnd?: (position: [number, number]) => void;
  iconType?: 'default' | 'delivery';
}

export function MapMarker({
  position,
  color = '#ff6b00',
  size = 28,
  onClick,
  popup,
  draggable = false,
  onDragEnd,
  iconType = 'default',
}: MapMarkerProps) {
  const map = useMapContext();
  const markerRef = useRef<MapLibreMarker | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    const [lng, lat] = position;
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return;
    }

    const el = document.createElement('div');
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.cursor = draggable ? 'grab' : onClick ? 'pointer' : 'default';

    if (iconType === 'delivery') {
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}">
          <circle cx="32" cy="32" r="30" fill="#ff6b00"/>
          <path d="M32 50 C32 50 46 36 46 26 C46 18 39 12 32 12 C25 12 18 18 18 26 C18 36 32 50 32 50Z" fill="white"/>
          <circle cx="32" cy="26" r="8" fill="#ff6b00"/>
        </svg>
      `;
    } else {
      el.style.borderRadius = '50%';
      el.style.backgroundColor = color;
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
    }

    const markerOptions: MarkerOptions = {
      element: el,
      anchor: 'center',
      draggable,
    };

    const marker = new MapLibreMarker(markerOptions).setLngLat(position).addTo(map);

    if (popup) {
      marker.setPopup(new Popup({ offset: 20 }).setText(popup));
    }

    if (onClick) {
      el.addEventListener('click', onClick);
    }

    if (draggable && onDragEnd) {
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        onDragEnd([lngLat.lng, lngLat.lat]);
      });
    }

    markerRef.current = marker;

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, [map, position, color, size, onClick, popup, draggable, onDragEnd, iconType]);

  useEffect(() => {
    if (!markerRef.current) {
      return;
    }
    markerRef.current.setLngLat(position);
  }, [position]);

  return null;
}
